/**
 * `/gallery/matchup-live` exists so
 * `tests/e2e/src/app/matchupLiveGeometry.e2e.ts` can measure ISSUE-989's
 * in-game Matchup in a real browser: no sideways scroll and no clipped leader
 * stat at 390px, two columns side by side at 1440px. It is signed in and
 * linked from no navigation, following the `/gallery/week-strip` precedent.
 *
 * The fixtures are a worst case written as literals: an overtime line score,
 * five column football performer categories with long names and values, a
 * full set of team stats, long scoring plays, and a nine inning MLB final.
 * No fetch.
 *
 * The file is named `gallery_.matchup-live` so this route stays out of
 * `/gallery`'s tree: `/gallery` renders no Outlet, so a nested route would
 * never paint.
 */
import { createFileRoute } from "@tanstack/react-router";
import type { ReactNode } from "react";

import { requireSignedIn } from "../auth/requireSignedIn";
import { MatchupTab } from "../features/gameDetail/MatchupTab";
import type {
  typeLeaderCategory,
  typeLineScoreCell,
  typeMatchupSection,
  typeStatComparisonRow,
} from "../features/gameDetail/matchupTypes";
import type { typeEventOdds } from "../features/gameDetail/oddsLogic";
import type { typeSummaryEntry } from "../features/gameDetail/SummaryTab";

const SF = {
  teamId: "gallery-sf",
  name: "San Francisco 49ers",
  abbreviation: "SF",
  logoUrl: null,
};
const JAX = {
  teamId: "gallery-jax",
  name: "Jacksonville Jaguars",
  abbreviation: "JAX",
  logoUrl: null,
};
const NYY = {
  teamId: "gallery-nyy",
  name: "New York Yankees",
  abbreviation: "NYY",
  logoUrl: null,
};
const LAD = {
  teamId: "gallery-lad",
  name: "Los Angeles Dodgers",
  abbreviation: "LAD",
  logoUrl: null,
};

const NFL_ODDS: typeEventOdds = {
  spread: {
    favoriteTeamId: SF.teamId,
    points: -3.5,
    isLocked: true,
    result: "away",
  },
  moneyline: null,
  total: { points: 47.5, result: "under" },
};

const cells = (
  labels: readonly string[],
  away: readonly number[],
  home: readonly number[],
): typeLineScoreCell[] =>
  labels.map((label, index) => ({
    key: `p${String(index + 1)}`,
    label,
    awayValue: String(away[index]),
    homeValue: String(home[index]),
  }));

const performer = ({
  key,
  title,
  columns,
  awayName,
  awayValues,
  homeName,
  homeValues,
  awayPlayerId = null,
}: {
  key: string;
  title: string;
  columns: string[];
  awayName: string;
  awayValues: string[];
  homeName: string;
  homeValues: string[];
  awayPlayerId?: string | null;
}): typeLeaderCategory => ({
  key,
  title,
  columns,
  away: {
    name: awayName,
    jerseyNumber: null,
    headshotUrl: null,
    playerId: awayPlayerId,
    values: awayValues,
  },
  home: {
    name: homeName,
    jerseyNumber: null,
    headshotUrl: null,
    playerId: null,
    values: homeValues,
  },
});

const statRow = (
  key: string,
  label: string,
  awayValue: string,
  homeValue: string,
  awaySharePercent: number,
  leader: "away" | "home" | "even",
): typeStatComparisonRow => ({
  key,
  label,
  awayValue,
  homeValue,
  awaySharePercent,
  leader,
  awayRankLabel: null,
  homeRankLabel: null,
});

const entry = (
  key: string,
  periodLabel: string,
  clockLabel: string,
  teamAbbreviation: string,
  description: string,
  awayScore: number,
  homeScore: number,
): typeSummaryEntry => ({
  key,
  periodLabel,
  clockLabel,
  teamAbbreviation,
  description,
  awayScore,
  homeScore,
});

const NFL_SECTIONS: typeMatchupSection[] = [
  {
    key: "lineScore",
    title: "Line score",
    kind: "lineScore",
    rows: [],
    lineScore: {
      periods: cells(
        ["1", "2", "3", "4", "OT"],
        [10, 7, 0, 3, 3],
        [7, 3, 10, 0, 0],
      ),
      totals: [{ key: "total", label: "T", awayValue: "23", homeValue: "20" }],
      winnerSide: "away",
    },
  },
  {
    key: "topPerformers",
    title: "Top performers",
    kind: "leaders",
    rows: [],
    leaders: {
      categories: [
        performer({
          key: "passing",
          title: "Passing",
          columns: ["C/ATT", "YDS", "TD", "INT", "RTG"],
          awayName: "Maximilian Okonkwo-Barrett",
          awayValues: ["32/48", "412", "4", "2", "118.7"],
          awayPlayerId: "gallery-player-1",
          homeName: "Jordan Castellanos",
          homeValues: ["28/41", "356", "3", "1", "104.2"],
        }),
        performer({
          key: "rushing",
          title: "Rushing",
          columns: ["CAR", "YDS", "AVG", "TD", "LNG"],
          awayName: "Christian Washington-Lee",
          awayValues: ["24", "187", "7.8", "2", "64"],
          homeName: "Travis Etienne Junior",
          homeValues: ["19", "96", "5.1", "1", "31"],
        }),
        performer({
          key: "receiving",
          title: "Receiving",
          columns: ["REC", "TGT", "YDS", "TD", "LNG"],
          awayName: "Brandon Aiyuk Montgomery",
          awayValues: ["12", "15", "212", "3", "75"],
          homeName: "Christian Kirkpatrick",
          homeValues: ["9", "13", "143", "1", "48"],
        }),
      ],
    },
  },
  {
    key: "teamStats",
    title: "Team stats",
    kind: "statComparison",
    rows: [],
    statComparison: {
      rows: [
        statRow("totalYards", "Total yards", "611", "499", 55, "away"),
        statRow("passingYards", "Passing yards", "412", "356", 54, "away"),
        statRow("rushingYards", "Rushing yards", "199", "143", 58, "away"),
        statRow("firstDowns", "First downs", "31", "24", 56, "away"),
        statRow("thirdDown", "Third down", "9-14", "6-13", 60, "away"),
        statRow("turnovers", "Turnovers", "2", "1", 67, "home"),
        statRow("penalties", "Penalties", "11-105", "6-45", 65, "home"),
        statRow("possession", "Possession", "33:42", "26:18", 56, "away"),
      ],
    },
  },
  {
    key: "scoringSummary",
    title: "Scoring summary",
    kind: "scoringSummary",
    rows: [],
    scoringSummary: {
      entries: [
        entry(
          "play-1",
          "1st Quarter",
          "11:02",
          "SF",
          "Maximilian Okonkwo-Barrett 64 yard pass to Brandon Aiyuk Montgomery",
          7,
          0,
        ),
        entry(
          "play-2",
          "1st Quarter",
          "02:15",
          "JAX",
          "Travis Etienne Junior 31 yard run, extra point by the home kicker",
          7,
          7,
        ),
        entry(
          "play-3",
          "2nd Quarter",
          "08:40",
          "SF",
          "Field goal from 52 yards after a long drive that ate up the clock",
          10,
          7,
        ),
        entry(
          "play-4",
          "2nd Quarter",
          "00:04",
          "JAX",
          "Field goal from 48 yards as time ran out on the first half of play",
          10,
          10,
        ),
      ],
    },
  },
  {
    key: "gameDetails",
    title: "Game details",
    kind: "details",
    rows: [],
    details: {
      rows: [
        {
          key: "venue",
          label: "Venue",
          value: "Levi's Stadium, Santa Clara, California",
        },
        { key: "attendance", label: "Attendance", value: "71,512" },
        { key: "broadcast", label: "TV", value: "CBS" },
      ],
    },
  },
];

const MLB_SECTIONS: typeMatchupSection[] = [
  {
    key: "lineScore",
    title: "Line score",
    kind: "lineScore",
    rows: [],
    lineScore: {
      periods: cells(
        ["1", "2", "3", "4", "5", "6", "7", "8", "9"],
        [0, 2, 0, 0, 1, 0, 3, 0, 0],
        [1, 0, 0, 0, 0, 2, 0, 0, 0],
      ),
      totals: [
        { key: "runs", label: "R", awayValue: "6", homeValue: "3" },
        { key: "hits", label: "H", awayValue: "11", homeValue: "7" },
        { key: "errors", label: "E", awayValue: "1", homeValue: "0" },
      ],
      winnerSide: "away",
    },
  },
  {
    key: "topPerformers",
    title: "Top performers",
    kind: "leaders",
    rows: [],
    leaders: {
      categories: [
        performer({
          key: "pitching",
          title: "Pitching",
          columns: ["IP", "H", "ER", "K", "BB"],
          awayName: "Gerrit Cole",
          awayValues: ["7.0", "4", "2", "11", "1"],
          homeName: "Yoshinobu Yamamoto",
          homeValues: ["5.1", "8", "5", "6", "3"],
        }),
        performer({
          key: "batting",
          title: "Batting",
          columns: ["H", "HR", "RBI", "SB"],
          awayName: "Aaron Judge",
          awayValues: ["3", "2", "5", "1"],
          homeName: "Shohei Ohtani",
          homeValues: ["2", "1", "2", "0"],
        }),
      ],
    },
  },
  {
    key: "teamStats",
    title: "Team stats",
    kind: "statComparison",
    rows: [],
    statComparison: {
      rows: [
        statRow("hits", "Hits", "11", "7", 61, "away"),
        statRow("errors", "Errors", "1", "0", 100, "home"),
      ],
    },
  },
  {
    key: "scoringSummary",
    title: "Scoring summary",
    kind: "scoringSummary",
    rows: [],
    scoringSummary: {
      entries: [
        entry(
          "play-1",
          "1st Inning",
          "Inn 1",
          "LAD",
          "Shohei Ohtani homers to right center field",
          0,
          1,
        ),
        entry(
          "play-2",
          "2nd Inning",
          "Inn 2",
          "NYY",
          "Aaron Judge doubles to left, two runs score",
          2,
          1,
        ),
        entry(
          "play-3",
          "2nd Inning",
          "Inn 2",
          "NYY",
          "Run scores on a sacrifice fly to deep center",
          3,
          1,
        ),
      ],
    },
  },
  {
    key: "gameDetails",
    title: "Game details",
    kind: "details",
    rows: [],
    details: {
      rows: [
        { key: "venue", label: "Venue", value: "Dodger Stadium" },
        { key: "attendance", label: "Attendance", value: "52,114" },
      ],
    },
  },
];

const MatchupLiveGalleryScreen = (): ReactNode => (
  <div className="flex flex-col gap-6 px-page-gutter py-4">
    <h1 className="text-h1 text-ink">Live matchup gallery</h1>
    <p className="text-body text-ink-secondary">
      Two in-game Matchup tabs filled with long names and wide stat rows. This
      page exists so a browser can measure the layout on a phone and on a wide
      screen. It is not linked from any navigation.
    </p>

    <div
      data-testid="matchup-live-gallery-wide"
      className="flex flex-col gap-2"
    >
      <h2 className="text-h2 text-ink">NFL final, wide</h2>
      <MatchupTab
        sections={NFL_SECTIONS}
        odds={NFL_ODDS}
        away={SF}
        home={JAX}
        isWide
      />
    </div>

    <div
      data-testid="matchup-live-gallery-narrow"
      className="flex flex-col gap-2"
    >
      <h2 className="text-h2 text-ink">MLB final, narrow</h2>
      <MatchupTab
        sections={MLB_SECTIONS}
        odds={null}
        away={NYY}
        home={LAD}
        isWide={false}
      />
    </div>
  </div>
);

export const Route = createFileRoute("/gallery_/matchup-live")({
  beforeLoad: requireSignedIn,
  component: MatchupLiveGalleryScreen,
});
